import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { DEFAULT_THRESHOLDS, loadAiSettings } from '../../utils/aiThresholds';
import useAutocomplete from '../../hooks/useAutocomplete';
import ZoomableImage from '../../components/ZoomableImage';
import MyAiHistory from '../../components/MyAiHistory';
import { TaxonDiagnosticPanel } from '../../components/TaxonDiagnostics';
import SpotterGame from '../spotter/SpotterGame';

/**
 * Expert model verification (/tools/verification; until 2026-10-13 the
 * "Identify game" at /identify-game, which redirects here): review what the
 * model predicted and record the truth. The mechanics, API and tables are the
 * game's (routes/image_quality.py); the framing changed, and one hook was added
 * so that "your verdicts feed the next model" is literally true.
 *
 * A low confidence has two causes -- a hard specimen or a bad photograph --
 * and only a person can tell them apart. Each photograph gets one verdict:
 *
 *   clear         fine photograph, hard specimen
 *   low_clarity   blurred, dark, distant: training down-weights it
 *   no_mushroom   nothing to identify: training and indexing skip it
 *
 * plus an optional "needs segmentation" flag and a note. The verdicts are
 * stored per person and exported to the sidecar file training reads
 * (backend/ai/datasets/quality_sidecar.py decide()).
 *
 * The blind genus: before the dataset's label is shown, the reviewer may name
 * the genus. A genus that differs from the label's DISPUTES the label, and a
 * disputed label is excluded from training, never replaced by the majority
 * guess (backend/ai/datasets/active_learning.py). The model's predictions are
 * HIDDEN until the reviewer asks for them; a genus given after looking is
 * recorded as seen-after (saw_ai_first) and does not count as a blind verdict.
 *
 * The correction hook (CORRECTION_ROLES): an expert may also name the taxon
 * the model should have given. It is sent on save to
 * POST /ai/identifications/{ai_id}/feedback (routes/ai.py record_feedback),
 * with saw_ai_first telling whether the prediction had been opened. A blind
 * correction is the one taxon-correction source MLOps reads for retraining
 * (routes/mlops.py, source `ai_review`: NOT saw_ai_first AND expert_taxon_name);
 * a seen-after one is recorded with its agreement and left out of training.
 *
 * After answering, the reveal shows the reference and literature data for the
 * dataset label (and for the model's top prediction when it names another
 * genus), and asks whether the label looks right. Reviewers can reset their
 * own verdicts and AI feedback from "My reviews and AI feedback"; official
 * identifications are never touched.
 */
export const THRESHOLDS = DEFAULT_THRESHOLDS.game_queue_levels;
export const QUEUE_SIZE = 10;
const VERDICTS = ['clear', 'low_clarity', 'no_mushroom'];
const LABEL_VERDICTS = ['confirm', 'reject', 'unsure'];
const EMPTY_FORM = { verdict: '', needsSegmentation: false, guess: '', correction: '', notes: '' };

/**
 * Who may record the taxon the model should have given. The route admits
 * every role that may request an identification (routes/ai.py REQUEST_ROLES),
 * but a blind expert_taxon_name becomes a training label (routes/mlops.py
 * `ai_review`), so the control is offered only to the roles the Expert review
 * page trusts with the same call (pages/ReviewQueue.js EXPERT_ROLES).
 */
export const CORRECTION_ROLES = ['collection_manager', 'internal_expert', 'lab_manager'];

/**
 * The certainty bands the queue offers, as {key, min, max}.
 *
 * The configured levels are the ones the queue has always had -- "less than
 * 30% sure", and so on. `certain` and `all` are the reason this is a band
 * rather than a ceiling: an expert checking whether the model made a DEFINITE
 * wrong identification needs the photographs it was most sure of, and those
 * were the one kind the queue could not offer.
 */
export function bands({ game_queue_levels: levels, high_certainty_min: certain }) {
  return [
    ...levels.map(max => ({ key: `under:${max}`, min: 0, max })),
    { key: 'certain', min: certain, max: 1 },
    { key: 'all', min: 0, max: 1 },
  ];
}

export const bandLabel = (t, band) => (
  band.key === 'certain' ? t('bandCertain', { percent: Math.round(band.min * 100) })
    : band.key === 'all' ? t('bandAll')
      : t('thresholdOption', { percent: Math.round(band.max * 100) }));

const percent = (value) => (value === null || value === undefined ? '?' : Math.round(value * 100));
const genusOf = (name) => (name || '').trim().split(/\s+/)[0] || '';
const normalise = (name) => (name || '').trim().toLowerCase().split(/\s+/).join(' ');

/**
 * How a taxon named AFTER seeing the model's predictions compares with its
 * top one: the rule of routes/ai.py blind_agreement, which the server applies
 * only to a blind answer. A seen-after answer has to state its agreement.
 */
export function agreementWith(taxon, guesses) {
  const top = normalise(guesses?.[0]?.taxon_name);
  const mine = normalise(taxon);
  if (!top || !mine) return 'unsure';
  if (top === mine) return 'agree';
  return top.split(' ')[0] === mine.split(' ')[0] ? 'partial' : 'disagree';
}

/**
 * Two tabs: the prediction review (the default) and the timed detection drill
 * (pages/spotter/SpotterGame.js), which calibrates the eye against the
 * annotation workbench's consensus boxes.
 */
export default function ModelVerification() {
  const { t } = useTranslation('game');
  const { user } = useAuth();
  const [mode, setMode] = useState('classic');
  const allowed = (user?.roles || []).some(r => r !== 'public');
  return (
    <div>
      {allowed && (
        <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {['classic', 'spotter'].map(m => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} data-testid={`game-mode-${m}`}
                    title={t(`modes.${m}Tip`)} onClick={() => setMode(m)}
                    style={{ fontWeight: mode === m ? 700 : 400 }}>
              {t(`modes.${m}`)}
            </button>
          ))}
        </div>
      )}
      {mode === 'spotter' && allowed ? <SpotterGame /> : <ClassicGame />}
    </div>
  );
}

export function ClassicGame() {
  const { t } = useTranslation('game');
  // `t` can be a new function on every render (it is, without an i18n instance).
  // Reading it through a ref keeps the loaders stable, so a change of `t`
  // cannot refetch in a loop.
  const tRef = useRef(t);
  tRef.current = t;
  const { user } = useAuth();
  const roles = user?.roles || [];
  const allowed = roles.some(r => r !== 'public');
  const canCorrect = roles.some(r => CORRECTION_ROLES.includes(r));

  const [levels, setLevels] = useState(() => bands(DEFAULT_THRESHOLDS));
  const [bandKey, setBandKey] = useState('under:0.5');
  const [country, setCountry] = useState('');
  const [source, setSource] = useState('');
  // What the current band actually holds, so a dropdown never offers a country
  // with nothing behind it. Answered by the queue itself.
  const [options, setOptions] = useState({ countries: [], sources: [] });
  const [queue, setQueue] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [me, setMe] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [hintShown, setHintShown] = useState(false);
  const [labelState, setLabelState] = useState({ verdict: null, status: '' });
  const [correction, setCorrection] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // The taxon the model should have given, searched as the expert types. The
  // typed text lives in `form.correction`; the hook only supplies suggestions.
  const taxa = useAutocomplete({
    search: term => api.get('/taxonomy/search', { params: { q: term } }).then(r => r.data || []),
    onType: value => setForm(f => ({ ...f, correction: value })),
  });

  const loadQueue = useCallback((band, filters = {}) => {
    setLoading(true);
    setError('');
    const params = { threshold: band.max, min_confidence: band.min, limit: QUEUE_SIZE };
    // Omitted rather than sent empty: the backend reads an absent filter as
    // "any", and an empty string as a value it would have to refuse.
    if (filters.country) params.country = filters.country;
    if (filters.source) params.source = filters.source;
    return api.get('/ai/quality/queue', { params })
      .then(({ data }) => {
        setQueue(data.items || []);
        setRemaining(data.remaining || 0);
        setOptions({
          countries: data.filters?.countries || [],
          sources: data.filters?.sources || [],
        });
        if (data.me) setMe(data.me);
      })
      .catch(() => { setQueue([]); setError(tRef.current('loadFailed')); })
      .finally(() => setLoading(false));
  }, []);

  const loadLeaderboard = useCallback(() => api.get('/ai/quality/leaderboard')
    .then(({ data }) => setPlayers(data.players || []))
    .catch(() => {}), []);

  // The levels a collection manager configured. The current level only moves if
  // it is no longer offered, so a normal load does not fetch the queue twice.
  useEffect(() => {
    if (!allowed) return undefined;
    let live = true;
    loadAiSettings().then(({ thresholds }) => {
      if (!live) return;
      const offered = bands(thresholds);
      setLevels(offered);
      setBandKey(current => (offered.some(b => b.key === current) ? current
        : offered[Math.floor(thresholds.game_queue_levels.length / 2)].key));
    });
    return () => { live = false; };
  }, [allowed]);

  // Read through a ref for the same reason `t` is: `levels` is a NEW array
  // every time the settings resolve, so depending on it would refetch the
  // queue on a load that changed nothing. What the reader picked is `bandKey`,
  // and that is what the effect below watches.
  const levelsRef = useRef(levels);
  levelsRef.current = levels;
  const bandOf = useCallback(
    key => levelsRef.current.find(b => b.key === key) || levelsRef.current[0], []);
  const band = bandOf(bandKey);

  useEffect(() => {
    if (!allowed) return;
    loadQueue(bandOf(bandKey), { country, source });
    loadLeaderboard();
  }, [allowed, bandKey, country, source, bandOf, loadQueue, loadLeaderboard]);

  const current = queue[0];

  const next = () => {
    setForm(EMPTY_FORM);
    taxa.reset();
    setReveal(null);
    setHintShown(false);
    setLabelState({ verdict: null, status: '' });
    setCorrection(null);
    if (queue.length <= 1) loadQueue(band, { country, source });
    else setQueue(q => q.slice(1));
  };

  /**
   * Record the taxon the model should have given, against its identification.
   * `blind` is whether the prediction was still hidden when the taxon was
   * typed: a blind row is scored by the server and feeds retraining; a
   * seen-after one must state its agreement and is left out of training.
   */
  const recordCorrection = async ({ aiId, taxon, blind, guesses }) => {
    setCorrection({ status: 'saving', aiId, taxon, blind, guesses });
    try {
      await api.post(`/ai/identifications/${encodeURIComponent(aiId)}/feedback`, {
        expert_taxon_name: taxon,
        saw_ai_first: !blind,
        ...(blind ? {} : { agreement: agreementWith(taxon, guesses) }),
      });
      setCorrection({ status: 'recorded', aiId, taxon, blind, guesses });
    } catch {
      setCorrection({ status: 'failed', aiId, taxon, blind, guesses });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !form.verdict) return;
    setSaving(true);
    setError('');
    try {
      const noMushroom = form.verdict === 'no_mushroom';
      const corrected = noMushroom || !canCorrect || !current.ai_id ? '' : form.correction.trim();
      // Whether the prediction was still hidden when this answer was typed.
      const blind = !hintShown;
      const { data } = await api.post('/ai/quality/annotations', {
        photo_id: current.photo_id,
        ai_id: current.ai_id,
        verdict: form.verdict,
        needs_segmentation: noMushroom ? false : form.needsSegmentation,
        // A correction names the genus too, so an expert need not type it twice.
        guessed_genus: noMushroom ? undefined : (genusOf(form.guess) || genusOf(corrected) || undefined),
        notes: form.notes.trim() || undefined,
        saw_ai_first: hintShown,
      });
      setReveal({ ...(data.reveal || {}), annotation_id: data.annotation_id });
      if (data.me) setMe(data.me);
      setRemaining(r => Math.max(0, r - 1));
      loadLeaderboard();
      if (corrected) await recordCorrection({ aiId: current.ai_id, taxon: corrected, blind, guesses: current.ai_guesses || [] });
    } catch (err) {
      setError(err?.response?.data?.error || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const checkLabel = async (verdict) => {
    if (!reveal?.annotation_id) return;
    setLabelState({ verdict, status: 'saving' });
    try {
      await api.put(`/ai/quality/annotations/${reveal.annotation_id}/label`, { label_verdict: verdict });
      setLabelState({ verdict, status: 'saved' });
    } catch {
      setLabelState({ verdict: null, status: 'failed' });
    }
  };

  const historyChanged = (stats) => {
    if (stats) setMe(stats);
    loadLeaderboard();
    loadQueue(band, { country, source });
  };

  if (!allowed) {
    return (
      <div style={pageStyle}>
        <h2 style={titleStyle}>{t('title')}</h2>
        <p style={noticeStyle}>{t('notPermitted')}</p>
      </div>
    );
  }

  const noMushroom = form.verdict === 'no_mushroom';
  const guesses = current?.ai_guesses || [];
  const revealGuesses = reveal?.ai_guesses?.length ? reveal.ai_guesses : guesses;
  const label = reveal?.dataset_label;
  const aiTop = revealGuesses[0]?.taxon_name;
  const aiDiffers = aiTop && genusOf(aiTop).toLowerCase() !== genusOf(label).toLowerCase();
  const correctable = canCorrect && Boolean(current?.ai_id);

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={titleStyle}>{t('title')}</h2>
          <p style={introStyle}>{t('intro')}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          {me && (
            <div data-testid="game-score" style={scoreStyle}>
              {t('me', { points: me.points, reviews: me.reviews, correct: me.correct_genus })}
            </div>
          )}
          <button type="button" data-testid="game-history-toggle" onClick={() => setHistoryOpen(o => !o)} title={t('tip.historyToggle')} style={outlineBtn}>
            {historyOpen ? t('history.close') : t('history.open')}
          </button>
        </div>
      </div>

      {historyOpen && <MyAiHistory onChanged={historyChanged} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
        <label htmlFor="game-threshold" style={{ fontSize: 13 }}>{t('threshold')}</label>
        <select id="game-threshold" value={bandKey} onChange={e => setBandKey(e.target.value)} title={t('tip.threshold')} style={selectStyle}>
          {levels.map(level => (
            <option key={level.key} value={level.key}>{bandLabel(t, level)}</option>
          ))}
        </select>

        {/* Country and repository. Both list only what the chosen band holds,
            so a filter can never be picked that empties the queue. */}
        <label htmlFor="game-country" style={{ fontSize: 13 }}>{t('country')}</label>
        <select id="game-country" data-testid="game-country" value={country}
                onChange={e => setCountry(e.target.value)} title={t('tip.country')} style={selectStyle}>
          <option value="">{t('anyCountry')}</option>
          {options.countries.map(({ value, count }) => (
            <option key={value} value={value}>{`${value} (${count})`}</option>
          ))}
        </select>

        <label htmlFor="game-source" style={{ fontSize: 13 }}>{t('repository')}</label>
        <select id="game-source" data-testid="game-source" value={source}
                onChange={e => setSource(e.target.value)} title={t('tip.repository')} style={selectStyle}>
          <option value="">{t('anyRepository')}</option>
          {options.sources.map(({ value, count }) => (
            <option key={value} value={value}>{`${t(`source.${value}`)} (${count})`}</option>
          ))}
        </select>

        {(country || source) && (
          <button type="button" data-testid="game-clear-filters"
                  onClick={() => { setCountry(''); setSource(''); }} title={t('tip.clearFilters')} style={outlineBtn}>
            {t('clearFilters')}
          </button>
        )}
        {!loading && <span style={hintStyle}>{t('remaining', { count: remaining })}</span>}
      </div>

      {error && <p style={errorStyle}>{error}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 16 }} className="game-layout">
        <div>
          {loading ? <p style={hintStyle}>{t('loading')}</p> : !current ? (
            <div style={cardStyle}>
              <p>{t('empty')}</p>
              <button type="button" onClick={() => loadQueue(band, { country, source })} title={t('tip.reload')} style={btn('var(--c-primary)')}>{t('reload')}</button>
            </div>
          ) : (
            <div data-testid="game-card" style={cardStyle}>
              <ZoomableImage key={current.photo_id} src={resolvePhotoUrl(current.s3_url)} alt={t('photoAlt')}
                             frameStyle={{ borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}
                             imageStyle={{ maxWidth: '100%', maxHeight: '60vh' }} keyboard={false} inline />
              {/* Outlining is the annotation workbench's job (pages/tools/AnnotationWorkbench.js);
                  this opens that photograph there, outside its queue. */}
              <Link data-testid="game-annotate" title={t('tip.annotate')} to={`/tools/annotation?photo=${encodeURIComponent(current.photo_id)}`}
                    style={{ ...outlineBtn, marginBottom: 8, display: 'inline-block', textDecoration: 'none' }}>
                {t('annotateMushroom')}
              </Link>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: 'var(--c-text-body-fg)', marginBottom: 8 }}>
                <strong data-testid="game-ai-confidence">
                  {current.confidence === null || current.confidence === undefined
                    ? t('aiUnseen')
                    : t('aiUnsure', { percent: percent(current.confidence) })}
                </strong>
                {current.record_code && <span dir="ltr" style={{ color: 'var(--c-text-muted-fg)' }}>{current.record_code}</span>}
                {current.source && <span data-testid="game-item-source" style={{ color: 'var(--c-text-muted-fg)' }}>{t(`source.${current.source}`)}</span>}
                {current.country && <span dir="ltr" style={{ color: 'var(--c-text-muted-fg)' }}>{current.country}</span>}
                {current.reviews > 0 && <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('reviewsSoFar', { count: current.reviews })}</span>}
              </div>

              {!reveal ? (
                <form onSubmit={submit}>
                  {guesses.length > 0 && (
                    hintShown ? (
                      <div style={hintBoxStyle}>
                        <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('aiGuesses')}</div>
                        <ol data-testid="game-ai-guesses" style={{ margin: '2px 0 4px', paddingInlineStart: 20, fontSize: 14 }}>
                          {guesses.map(g => (
                            <li key={g.taxon_name}><em dir="ltr">{g.taxon_name}</em> — {percent(g.confidence)}%</li>
                          ))}
                        </ol>
                        <div data-testid="game-hint-shown" style={{ fontSize: 12, color: 'var(--c-warning-text-fg)' }}>{t('hint.shown')}</div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        <button type="button" data-testid="game-ai-hint" onClick={() => setHintShown(true)} title={t('tip.hint')} style={outlineBtn}>
                          {t('hint.show')}
                        </button>
                        <span style={hintStyle}>{t('hint.note')}</span>
                      </div>
                    )
                  )}

                  <fieldset style={fieldsetStyle}>
                    <legend style={{ fontWeight: 600 }}>{t('question')}</legend>
                    {VERDICTS.map(v => (
                      <label key={v} style={optionStyle(form.verdict === v)}>
                        <input type="radio" name="verdict" value={v} checked={form.verdict === v}
                               title={{
                                 clear: t('tip.verdict.clear'),
                                 low_clarity: t('tip.verdict.low_clarity'),
                                 no_mushroom: t('tip.verdict.no_mushroom'),
                               }[v]}
                               onChange={() => setForm(f => ({ ...f, verdict: v }))} />
                        <span>
                          <span style={{ display: 'block', fontWeight: 600 }}>{t(`verdict.${v}`)}</span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t(`verdictHint.${v}`)}</span>
                        </span>
                      </label>
                    ))}
                  </fieldset>

                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, margin: '8px 0', opacity: noMushroom ? 0.5 : 1 }}>
                    <input type="checkbox" checked={form.needsSegmentation && !noMushroom} disabled={noMushroom}
                           title={t('tip.needsSegmentation')}
                           onChange={e => setForm(f => ({ ...f, needsSegmentation: e.target.checked }))} />
                    <span>{t('needsSegmentation')}</span>
                  </label>

                  <label style={labelStyle} htmlFor="game-guess">{t('guess')}</label>
                  <input id="game-guess" list="game-guess-options" value={form.guess} disabled={noMushroom}
                         onChange={e => setForm(f => ({ ...f, guess: e.target.value }))}
                         title={t('tip.guess')}
                         placeholder={t('guessPlaceholder')} style={inputStyle} dir="ltr" />
                  <datalist id="game-guess-options">
                    {hintShown && [...new Set(guesses.map(g => genusOf(g.taxon_name)))].map(g => <option key={g} value={g} />)}
                  </datalist>
                  <p style={{ ...hintStyle, margin: '2px 0 0' }}>{t('guessHint')}</p>

                  {/* The retraining hook: the taxon the model should have given,
                      for the roles that may correct it. Sent on save, against
                      this photograph's identification. */}
                  {correctable && (
                    <div data-testid="game-correction" style={{ opacity: noMushroom ? 0.5 : 1 }}>
                      <label style={labelStyle} htmlFor="game-correction">{t('correction.label')}</label>
                      <input id="game-correction" list="game-correction-options" value={form.correction} disabled={noMushroom}
                             onChange={taxa.onChange} autoComplete="off" title={t('tip.correction')}
                             placeholder={t('correction.placeholder')} style={inputStyle} dir="ltr" />
                      <datalist id="game-correction-options">
                        {taxa.suggestions.map(s => <option key={s.taxon_id || s.scientific_name} value={s.scientific_name} />)}
                      </datalist>
                      <p data-testid="game-correction-note" style={{ ...hintStyle, margin: '2px 0 0' }}>
                        {hintShown ? t('correction.seenNote') : t('correction.blindNote')}
                      </p>
                    </div>
                  )}

                  <label style={labelStyle} htmlFor="game-notes">{t('notes')}</label>
                  <textarea id="game-notes" value={form.notes} rows={2} maxLength={2000}
                            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                            title={t('tip.notes')}
                            placeholder={form.verdict === 'low_clarity' ? t('notesLowClarity') : ''}
                            style={{ ...inputStyle, resize: 'vertical' }} />

                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button type="submit" disabled={!form.verdict || saving} title={t('tip.submit')} style={btn('var(--c-primary)')}>
                      {saving ? t('saving') : t('submit')}
                    </button>
                    <button type="button" onClick={next} disabled={saving} title={t('tip.skip')} style={btn('var(--c-text-muted)')}>{t('skip')}</button>
                  </div>
                </form>
              ) : (
                <div data-testid="game-reveal" style={revealStyle}>
                  <strong>{t('reveal.title')}</strong>
                  {label
                    ? <p style={{ margin: '6px 0 2px' }}>{t('reveal.label', { label })}</p>
                    : <p style={{ margin: '6px 0 2px' }}>{t('reveal.noLabel')}</p>}
                  {label && <p style={{ ...hintStyle, margin: '0 0 6px' }}>{t('labelIsDatasetLabel')}</p>}
                  <p style={{ margin: '4px 0', fontWeight: 600, color: reveal.guess_correct && reveal.scored !== false ? 'var(--c-success-2-fg)' : 'var(--c-text-body-fg)' }}>
                    {reveal.guess_correct === true && reveal.scored !== false && t('reveal.correct')}
                    {reveal.guess_correct === false && t('reveal.wrong', { genus: reveal.genus })}
                    {(reveal.guess_correct === null || reveal.guess_correct === undefined) && t('reveal.noGuess')}
                  </p>
                  {reveal.scored === false && <p data-testid="game-not-scored" style={{ ...hintStyle, color: 'var(--c-warning-text-fg)' }}>{t('reveal.notScored')}</p>}

                  {correction && (
                    <p data-testid="game-correction-status" data-status={correction.status} role={correction.status === 'failed' ? 'alert' : 'status'}
                       style={correction.status === 'failed' ? { ...errorStyle, margin: '6px 0' } : { ...hintStyle, margin: '6px 0', fontWeight: 600, color: 'var(--c-success-2-fg)' }}>
                      {correction.status === 'saving' && t('correction.saving')}
                      {correction.status === 'recorded' && (correction.blind
                        ? t('correction.recorded', { taxon: correction.taxon })
                        : t('correction.recordedSeen', { taxon: correction.taxon }))}
                      {correction.status === 'failed' && (
                        <>
                          {t('correction.failed', { taxon: correction.taxon })}{' '}
                          <button type="button" data-testid="game-correction-retry" onClick={() => recordCorrection(correction)}
                                  title={t('tip.correctionRetry')} style={{ ...outlineBtn, padding: '2px 8px', fontSize: 12 }}>
                            {t('correction.retry')}
                          </button>
                        </>
                      )}
                    </p>
                  )}

                  {revealGuesses.length > 0 && (
                    <div style={{ margin: '6px 0' }}>
                      <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('reveal.aiGuesses')}</div>
                      <ol data-testid="game-reveal-guesses" style={{ margin: '2px 0 0', paddingInlineStart: 20, fontSize: 13 }}>
                        {revealGuesses.map(g => (
                          <li key={g.taxon_name}><em dir="ltr">{g.taxon_name}</em> — {percent(g.confidence)}%</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '8px 0' }}>
                    {label && <TaxonDiagnosticPanel name={label} heading={t('reveal.checkLabel')} testId="game-label-diagnostics" />}
                    {aiDiffers && <TaxonDiagnosticPanel name={aiTop} heading={t('reveal.checkAi')} testId="game-ai-diagnostics" />}
                  </div>

                  {label && reveal.annotation_id && (
                    <div data-testid="game-label-check" style={{ margin: '8px 0' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t('reveal.labelQuestion')}</div>
                      <div role="group" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {LABEL_VERDICTS.map(v => (
                          <button key={v} type="button" aria-pressed={labelState.verdict === v}
                                  disabled={labelState.status === 'saving'} onClick={() => checkLabel(v)}
                                  title={{
                                    confirm: t('tip.labelVerdict.confirm'),
                                    reject: t('tip.labelVerdict.reject'),
                                    unsure: t('tip.labelVerdict.unsure'),
                                  }[v]}
                                  style={labelBtn(labelState.verdict === v)}>
                            {t(`reveal.labelVerdict.${v}`)}
                          </button>
                        ))}
                      </div>
                      {labelState.status === 'saved' && <p role="status" style={hintStyle}>{t('reveal.labelSaved')}</p>}
                      {labelState.status === 'failed' && <p role="alert" style={errorStyle}>{t('reveal.labelFailed')}</p>}
                    </div>
                  )}
                  <button type="button" onClick={next} title={t('tip.next')} style={btn('var(--c-primary)')}>{t('next')}</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Who has verified what. Ordered by the server (its points, which are
            no longer shown here): a tally of contributions, not a ranking. */}
        <aside data-testid="leaderboard" style={cardStyle}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>{t('leaderboard')}</h3>
          {players.length === 0 ? <p style={hintStyle}>{t('noPlayers')}</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--c-text-muted-fg)', textAlign: 'start' }}>
                  <th style={cellStyle}>{t('player')}</th>
                  <th style={cellStyle}>{t('reviews')}</th>
                  <th style={cellStyle}>{t('correct')}</th>
                </tr>
              </thead>
              <tbody>
                {players.map(p => (
                  <tr key={p.user_id} style={p.is_me ? { background: 'var(--c-primary-soft-2)', fontWeight: 600 } : undefined}>
                    <td style={cellStyle}>{p.username}{p.is_me ? ` ${t('you')}` : ''}</td>
                    <td style={cellStyle}>{p.reviews}</td>
                    <td style={cellStyle}>{p.correct_genus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </aside>
      </div>
    </div>
  );
}

const pageStyle = { maxWidth: 1100, margin: '0 auto', padding: '8px 4px' };
const titleStyle = { margin: '0 0 6px', color: 'var(--c-primary-fg)' };
const introStyle = { fontSize: 14, color: 'var(--c-text-body-fg)', margin: '0 0 10px', lineHeight: 1.5, maxWidth: 720 };
const hintStyle = { fontSize: 13, color: 'var(--c-text-muted-fg)' };
const noticeStyle = { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '8px 12px' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '8px 12px' };
const scoreStyle = { background: 'var(--c-primary-soft-2)', color: 'var(--c-success-2-fg)', border: '1px solid var(--c-primary-border)', borderRadius: 8, padding: '6px 12px', fontSize: 14, fontWeight: 600 };
const selectStyle = { padding: '4px 8px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 13 };
const cardStyle = { background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10, padding: 12 };
const hintBoxStyle = { background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning-soft)', borderRadius: 8, padding: '6px 10px', marginBottom: 10 };
const fieldsetStyle = { border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 };
const optionStyle = (on) => ({
  display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
  border: `1px solid ${on ? 'var(--c-primary)' : 'var(--c-border)'}`, background: on ? 'var(--c-primary-soft-2)' : 'var(--c-surface)',
});
const labelStyle = { display: 'block', fontSize: 13, color: 'var(--c-text-body-fg)', margin: '8px 0 2px' };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14 };
const revealStyle = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 10 };
const cellStyle = { padding: '4px 6px', borderBottom: '1px solid var(--c-bg-muted)', textAlign: 'start' };
const btn = (bg) => ({ background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 14 });
const outlineBtn = { background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 13 };
const labelBtn = (on) => ({
  background: on ? 'var(--c-primary)' : 'var(--c-surface)', color: on ? 'var(--c-on-accent)' : 'var(--c-text-body-fg)',
  border: `1px solid ${on ? 'var(--c-primary)' : 'var(--c-border-strong)'}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 13,
});
